// components/billings/expense-detail.component.jsx
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { createPortal } from "react-dom";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { useSelector } from "react-redux";

import {
  FiCalendar,
  FiCheck,
  FiX,
} from "react-icons/fi";

import {
  db,
  fieldValue,
} from "../../firebase/firebase.utils";

import CurrencyPicker from "./currency-picker.component";

import {
  CURRENCIES,
  COMMON_CURRENCY_CODES,
} from "../../constants/currencies";

import "./expense-detail.styles.scss";

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

function normalizeCategory(value) {
  if (typeof value !== "string") return "";

  return value.trim().replace(/\s+/g, " ");
}

function safeNumberText(value) {
  const cleaned = String(value)
    .replace(",", ".")
    .replace(/[^0-9.]/g, "");

  const parts = cleaned.split(".");

  if (parts.length <= 2) {
    return cleaned;
  }

  return `${parts[0]}.${parts.slice(1).join("")}`;
}

function toDateInputValue(value) {
  if (!value) return "";

  let date;

  if (typeof value.toDate === "function") {
    date = value.toDate();
  } else if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(date.getDate()).padStart(
    2,
    "0"
  );

  return `${year}-${month}-${day}`;
}

function fromDateInputValue(value) {
  if (!value) return null;

  const [year, month, day] = value
    .split("-")
    .map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(
    year,
    month - 1,
    day,
    0,
    0,
    0,
    0
  );
}

/* -------------------------------------------------------------------------- */
/*                              Main component                                */
/* -------------------------------------------------------------------------- */

export default function ExpenseDetail({
  mode = "create",
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { wspaceid, expenseId } = useParams();

  const currentUser = useSelector(
    (state) => state.user.currentUser
  );

  const isEditMode =
    mode === "edit" && Boolean(expenseId);

  const backgroundPath =
    location.state?.backgroundPath;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [amount, setAmount] = useState("");
  const [currencyCode, setCurrencyCode] =
    useState("USD");

  const [description, setDescription] =
    useState("");

  const [category, setCategory] = useState("");
  const [vendor, setVendor] = useState("");

  const [attachmentUrl, setAttachmentUrl] =
    useState("");

  const [expenseDate, setExpenseDate] = useState(
    () => toDateInputValue(new Date())
  );

  const [paid, setPaid] = useState(false);

  const [categoryPool, setCategoryPool] =
    useState([]);

  const selectedCurrency = useMemo(
    () =>
      CURRENCIES.find(
        (currency) =>
          currency.code === currencyCode
      ) || null,
    [currencyCode]
  );

  const filteredCategoryPool = useMemo(() => {
    const query = category
      .trim()
      .toLowerCase();

    if (!query) {
      return categoryPool.slice(0, 8);
    }

    return categoryPool
      .filter((item) =>
        item.toLowerCase().includes(query)
      )
      .slice(0, 8);
  }, [category, categoryPool]);

  const closeExpense = () => {
    if (backgroundPath) {
      navigate(-1);
      return;
    }

    navigate(`/s/${wspaceid}`, {
      replace: true,
    });
  };

  /* ------------------------------- Modal UX ------------------------------- */

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      // CurrencyPicker handles Escape itself.
      if (
        document.querySelector(
          ".currency-picker__modal-backdrop"
        )
      ) {
        return;
      }

      closeExpense();
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [backgroundPath, navigate, wspaceid]);

  /* ----------------------------- Initial data ----------------------------- */

  useEffect(() => {
    if (!wspaceid) {
      setLoadError(
        "No workspace was provided for this expense."
      );

      setLoading(false);
      return;
    }

    let active = true;

    const loadInitialData = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const expensesRef = db
          .collection("space")
          .doc(wspaceid)
          .collection("expenses");

        const expensePromise = isEditMode
          ? expensesRef.doc(expenseId).get()
          : Promise.resolve(null);

        const categoriesPromise =
          expensesRef.get();

        const [
          expenseSnapshot,
          categoriesSnapshot,
        ] = await Promise.all([
          expensePromise,
          categoriesPromise,
        ]);

        if (!active) return;

        if (isEditMode) {
          if (!expenseSnapshot?.exists) {
            throw new Error(
              "This expense could not be found."
            );
          }

          applyExpenseToState(
            expenseSnapshot.data()
          );
        }

        const categories = [];
        const existingCategories = new Set();

        categoriesSnapshot.forEach(
          (document) => {
            const cleanedCategory =
              normalizeCategory(
                document.data()?.category
              );

            const normalizedKey =
              cleanedCategory.toLowerCase();

            if (
              cleanedCategory &&
              !existingCategories.has(
                normalizedKey
              )
            ) {
              existingCategories.add(
                normalizedKey
              );

              categories.push(cleanedCategory);
            }
          }
        );

        categories.sort((categoryA, categoryB) =>
          categoryA.localeCompare(categoryB)
        );

        setCategoryPool(categories);
      } catch (error) {
        console.error(
          "Expense initial data error:",
          error
        );

        if (active) {
          setLoadError(
            error?.message ||
              "The expense could not be loaded."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      active = false;
    };
  }, [wspaceid, expenseId, isEditMode]);

  function applyExpenseToState(expense) {
    setAmount(
      expense?.amount !== undefined &&
        expense?.amount !== null
        ? String(expense.amount)
        : ""
    );

    setCurrencyCode(
      expense?.currencyCode || "USD"
    );

    setDescription(
      expense?.description || ""
    );

    setCategory(expense?.category || "");
    setVendor(expense?.vendor || "");

    setAttachmentUrl(
      expense?.attachmentUrl || ""
    );

    setExpenseDate(
      toDateInputValue(expense?.date) ||
        toDateInputValue(new Date())
    );

    setPaid(Boolean(expense?.paid));
  }

  /* -------------------------------- Saving -------------------------------- */

  const handleSaveExpense = async (event) => {
    event.preventDefault();

    const parsedAmount = Number(amount);

    if (!wspaceid) {
      window.alert(
        "No workspace was provided."
      );

      return;
    }

    if (!description.trim()) {
      window.alert(
        "Please enter an expense description."
      );

      return;
    }

    if (
      !amount.trim() ||
      !Number.isFinite(parsedAmount) ||
      parsedAmount < 0
    ) {
      window.alert(
        "Please enter a valid expense amount."
      );

      return;
    }

    const parsedDate =
      fromDateInputValue(expenseDate);

    if (!parsedDate) {
      window.alert(
        "Please select a valid expense date."
      );

      return;
    }

    const payload = {
      amount: parsedAmount,
      currencyCode:
        currencyCode.trim().toUpperCase() ||
        "USD",

      description: description.trim(),
      category: normalizeCategory(category),
      vendor: vendor.trim(),

      attachmentUrl:
        attachmentUrl.trim(),

      date: parsedDate,
      paid,

      updatedAt:
        fieldValue.serverTimestamp(),

      lastUpdater:
        currentUser?.uid || null,
    };

    try {
      setSaving(true);

      const expensesRef = db
        .collection("space")
        .doc(wspaceid)
        .collection("expenses");

      if (isEditMode) {
        await expensesRef
          .doc(expenseId)
          .update(payload);
      } else {
        await expensesRef.add({
          ...payload,

          createdAt:
            fieldValue.serverTimestamp(),

          createdBy:
            currentUser?.uid || null,
        });
      }

      closeExpense();
    } catch (error) {
      console.error(
        "Expense save error:",
        error
      );

      window.alert(
        error?.message ||
          "The expense could not be saved."
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------- UI ---------------------------------- */

  const modalContent = (
    <div
      className="expense-detail__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          closeExpense();
        }
      }}
    >
      <section
        className="expense-detail__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="expense-detail-title"
      >
        <header className="expense-detail__header">
          <div>
            <h2 id="expense-detail-title">
              {isEditMode
                ? "Edit Expense"
                : "New Expense"}
            </h2>

            <span>
              {isEditMode
                ? "Update an outgoing payment"
                : "Record an outgoing payment"}
            </span>
          </div>

          <button
            type="button"
            onClick={closeExpense}
            aria-label="Close expense editor"
          >
            <FiX />
          </button>
        </header>

        {loading ? (
          <div className="expense-detail__centered">
            Loading expense…
          </div>
        ) : loadError ? (
          <div className="expense-detail__error">
            <p>{loadError}</p>

            <button
              type="button"
              className="expense-detail__secondary-button"
              onClick={closeExpense}
            >
              Close
            </button>
          </div>
        ) : (
          <form
            className="expense-detail__form"
            onSubmit={handleSaveExpense}
          >
            <main className="expense-detail__content">
              <Field
                label="Description"
                wide
              >
                <input
                  type="text"
                  value={description}
                  onChange={(event) =>
                    setDescription(
                      event.target.value
                    )
                  }
                  placeholder="Office chair, hosting fee, taxi..."
                  autoFocus={!isEditMode}
                />
              </Field>

              <div className="expense-detail__form-grid">
                <Field
                  label={`Amount${
                    selectedCurrency?.symbol
                      ? ` (${selectedCurrency.symbol})`
                      : ""
                  }`}
                >
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(event) =>
                      setAmount(
                        safeNumberText(
                          event.target.value
                        )
                      )
                    }
                    placeholder="0.00"
                  />
                </Field>

                <Field label="Date">
                  <div className="expense-detail__date-input">
                    <input
                      type="date"
                      value={expenseDate}
                      onChange={(event) =>
                        setExpenseDate(
                          event.target.value
                        )
                      }
                    />

                    <FiCalendar />
                  </div>
                </Field>
              </div>

              <CurrencyPicker
                value={currencyCode}
                onChange={(currency) =>
                  setCurrencyCode(currency.code)
                }
                currencies={CURRENCIES}
                commonCurrencyCodes={
                  COMMON_CURRENCY_CODES
                }
                label="Currency"
              />

              <Field label="Category" wide>
                <input
                  type="text"
                  value={category}
                  onChange={(event) =>
                    setCategory(
                      event.target.value
                    )
                  }
                  placeholder="Travel, Software, Supplies..."
                />

                {filteredCategoryPool.length >
                  0 && (
                  <div className="expense-detail__categories">
                    {filteredCategoryPool.map(
                      (categoryItem) => (
                        <button
                          type="button"
                          key={categoryItem}
                          className={
                            category.toLowerCase() ===
                            categoryItem.toLowerCase()
                              ? "expense-detail__category expense-detail__category--selected"
                              : "expense-detail__category"
                          }
                          onClick={() =>
                            setCategory(
                              categoryItem
                            )
                          }
                        >
                          {categoryItem}
                        </button>
                      )
                    )}
                  </div>
                )}
              </Field>

              <div className="expense-detail__form-grid">
                <Field label="Vendor">
                  <input
                    type="text"
                    value={vendor}
                    onChange={(event) =>
                      setVendor(
                        event.target.value
                      )
                    }
                    placeholder="Optional"
                  />
                </Field>

                <Field label="Attachment URL">
                  <input
                    type="url"
                    value={attachmentUrl}
                    onChange={(event) =>
                      setAttachmentUrl(
                        event.target.value
                      )
                    }
                    placeholder="Optional"
                  />
                </Field>
              </div>

              <label className="expense-detail__paid-card">
                <span>
                  <strong>
                    Expense has been paid
                  </strong>

                  <small>
                    Leave this unchecked to
                    track it as a pending expense.
                  </small>
                </span>

                <input
                  type="checkbox"
                  checked={paid}
                  onChange={(event) =>
                    setPaid(
                      event.target.checked
                    )
                  }
                />

                {paid && <FiCheck />}
              </label>
            </main>

            <footer className="expense-detail__footer">
              <button
                type="button"
                className="expense-detail__secondary-button"
                onClick={closeExpense}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="expense-detail__primary-button"
                disabled={saving}
              >
                {saving
                  ? "Saving…"
                  : isEditMode
                    ? "Save changes"
                    : "Create expense"}
              </button>
            </footer>
          </form>
        )}
      </section>
    </div>
  );

  return createPortal(
    modalContent,
    document.body
  );
}

/* -------------------------------------------------------------------------- */
/*                              Small component                               */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  children,
  wide = false,
}) {
  return (
    <label
      className={
        wide
          ? "expense-detail__field expense-detail__field--wide"
          : "expense-detail__field"
      }
    >
      <span>{label}</span>
      {children}
    </label>
  );
}